/**
 * Visão MAPA do setor: a sala em tamanho grande, com os SUBSETORES e os
 * POSTOS dentro dela — e é aqui que o gestor organiza a hierarquia
 * (setor > subsetor > posto) do jeito que a fábrica é de verdade.
 *
 * O que o gestor faz nesta tela:
 * - DESENHA à mão livre um subsetor ou um posto (o traço torto é corrigido por
 *   `dados/tracado.js`: reto fica reto, redondo fica redondo);
 * - ARRASTA e REDIMENSIONA o que já existe (dedo, mouse ou setas do teclado);
 * - MOVE um posto de um subsetor para outro só soltando-o lá dentro;
 * - RENOMEIA e DESATIVA pelo painel do que está selecionado;
 * - vê QUEM trabalha em cada posto e o que está alocado ali.
 *
 * O contorno da SALA não se edita aqui: ele é desenhado no mapa geral, onde o
 * gestor vê a fábrica inteira e a sala ao lado das outras. Aqui ela é o limite.
 *
 * Gravação: um rascunho local (nada vai ao backend a cada arraste) e um
 * "Salvar desenho" que manda tudo de uma vez — PUT /formas é tudo ou nada, e
 * o posto que trocou de subsetor vai no PATCH dele (a forma junto), senão a
 * segunda escrita bateria num 409 criado pela primeira.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiRotateCcw, FiSave } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { plural } from "../../../utils/dinheiro";
import { truncarTexto } from "../planta";
import {
  LIMITES_FORMA,
  caixaEnvolvente,
  mover,
  normalizar,
  pontoDentro,
  redimensionar,
} from "../dados/forma";
import { corrigirTracado } from "../dados/tracado";
import { ROTULO_STATUS_AMBIENTE, ROTULO_STATUS_PECA } from "../dados/vocabulario";
import { limparEspacos } from "./formularios";
import {
  ALVO,
  MODO,
  MODOS,
  motivoParaNaoExcluir,
  PASSO_TECLADO,
  cenaDoSetor,
  comForma,
  escalaDaTela,
  mudancasParaSalvar,
  paiDoPosto,
  paraMundo,
  textoDoRascunho,
  validarNoPai,
} from "./plantaDoSetor";
import { iniciais, textoDoPrazo } from "./modelo";
import {
  Acoes,
  AvisoMapa,
  BarraMapa,
  Campo,
  Espaco,
  ItemLista,
  LegendaMapa,
  Lista,
  PainelElemento,
  Planta,
  QuadroMapa,
} from "./styles";
import { ErroDaAcao, PillStatus, SeletorSegmentado, useErroDeAcao } from "./ui";

const TAMANHO_NOME = 120;
// Abaixo disto o gesto é um toque, não um desenho: seleciona em vez de criar.
const TRACO_MINIMO = 3;
const MAX_PESSOAS_NO_ROTULO = 3;
const MAX_ITENS_NO_PAINEL = 12;
// Piso da letra na planta: abaixo disto ninguém lê, e sumir com o nome da
// bancada é pior do que a letra pequena.
const MENOR_FONTE = 7;
// Letras que precisam sobrar para valer a pena mostrar o nome cortado.
const MINIMO_DO_NOME = 3;

const LISTA_VAZIA = [];

// Texto é medido no MUNDO (o SVG escala tudo junto): derivar o tamanho da
// largura da visão mantém tudo legível em sala grande ou pequena.
const fonteDaVisao = (visao) => Math.max(7, visao.caixa.largura / 70);
// Alça e traço, ao contrário, são medidos em PIXELS DE TELA e convertidos: o
// dedo não encolhe junto com a sala. 44 px é o alvo de toque da casa; o
// quadrado desenhado é menor, para não cobrir o que está embaixo.
const ALVO_ALCA_PX = 44;
const ALCA_VISIVEL_PX = 16;
const LARGURA_TRACO_PX = 4;
// Abaixo disto (pixels de tela) o gesto foi um toque, não um desenho.
const TRACO_MINIMO_PX = 24;

function caminhoDoTraco(pontos) {
  if (!pontos || pontos.length === 0) return "";
  const [primeiro, ...resto] = pontos;
  return `M ${primeiro.x} ${primeiro.y} ${resto.map((p) => `L ${p.x} ${p.y}`).join(" ")}`;
}

/** O elemento sob o ponto: posto antes de subsetor, e o menor antes do maior. */
function elementoNoPonto(cena, ponto) {
  const candidatos = [...cena.postos, ...cena.subsetores]
    .filter((e) => pontoDentro(e.forma, ponto))
    .sort((a, b) => (a.alvo === b.alvo ? (a.area ?? 0) - (b.area ?? 0) : a.alvo === ALVO.POSTO ? -1 : 1));
  return candidatos[0] || null;
}

function proximoNome(prefixo, quantos) {
  return `${prefixo} ${quantos + 1}`;
}

// ------------------------------------------------------------------ SVG

/**
 * Nome que cabe na caixa, ou "" quando o que sobraria não diz nada ("S…").
 * Caixa minúscula sem rótulo nenhum é mais limpa do que com uma letra e
 * reticências — quem precisa do nome toca e lê no painel ao lado.
 */
export function nomeQueCabe(nome, largura, fonte) {
  const texto = truncarTexto(nome, largura, fonte);
  if (!texto.endsWith("…")) return texto;
  return texto.length - 1 >= MINIMO_DO_NOME ? texto : "";
}

/**
 * Linhas do rótulo que a caixa aguenta, e a fonte que cabe nela.
 *
 * Bancada pequena com o nome inteiro e três linhas de contagem escorria por
 * cima da bancada do lado — a planta ficava ilegível justamente onde há mais
 * postos. Regra: a fonte encolhe até o limite do que a caixa comporta, e cada
 * linha extra só aparece se houver altura para ela.
 */
export function rotuloDaCaixa(elemento, fonte) {
  const { caixa, contagem = {} } = elemento || {};
  if (!caixa || caixa.largura <= 0 || caixa.altura <= 0) return null;
  const partes = [];
  if (contagem.ambientes) partes.push(plural(contagem.ambientes, "ambiente", "ambientes"));
  if (contagem.pecas) partes.push(plural(contagem.pecas, "peça", "peças"));
  if (contagem.tarefas) partes.push(plural(contagem.tarefas, "tarefa", "tarefas"));
  const base = elemento.alvo === ALVO.POSTO ? fonte * 0.92 : fonte;
  // Nunca mais alta que um terço da caixa: sobra espaço para as linhas de
  // baixo e para o dedo pegar a forma sem acertar a letra.
  const tamanho = Math.max(MENOR_FONTE, Math.min(base, caixa.altura / 3.2));
  const largura = caixa.largura - tamanho * 1.2;
  const inteiro = `${elemento.nome}${elemento.ativo ? "" : " (desativado)"}`;
  const nome = nomeQueCabe(inteiro, largura, tamanho);
  const cabeLinha = (n) => caixa.altura >= tamanho * (1.6 + 1.15 * n);
  return {
    x: caixa.x + tamanho * 0.6,
    y: caixa.y + tamanho * 1.4,
    tamanho,
    nome,
    contagem: partes.length && cabeLinha(1) ? truncarTexto(partes.join(" · "), largura, tamanho * 0.82) : "",
    atraso:
      contagem.atrasados > 0 && cabeLinha(2)
        ? truncarTexto(plural(contagem.atrasados, "atrasado", "atrasados"), largura, tamanho * 0.82)
        : "",
    pessoas: cabeLinha(3),
  };
}

function RotuloDoElemento({ elemento, fonte }) {
  const rotulo = rotuloDaCaixa(elemento, fonte);
  if (!rotulo) return null;
  const { x, y, tamanho } = rotulo;
  const pessoas = elemento.pessoas || LISTA_VAZIA;
  return (
    <>
      <text
        className={elemento.alvo === ALVO.POSTO ? "rotulo rotulo--posto" : "rotulo"}
        x={x}
        y={y}
        fontSize={tamanho}
      >
        {rotulo.nome}
      </text>
      {rotulo.contagem && (
        <text className="contagem" x={x} y={y + tamanho * 1.15} fontSize={tamanho * 0.82}>
          {rotulo.contagem}
        </text>
      )}
      {rotulo.atraso && (
        <text className="atraso" x={x} y={y + tamanho * 2.3} fontSize={tamanho * 0.82}>
          {rotulo.atraso}
        </text>
      )}
      {pessoas.length > 0 && rotulo.pessoas && (
        <text
          className="contagem"
          x={x}
          y={elemento.caixa.y + elemento.caixa.altura - tamanho * 0.5}
          fontSize={tamanho * 0.82}
        >
          {pessoas.slice(0, MAX_PESSOAS_NO_ROTULO).map((p) => iniciais(p.nome)).join(" ")}
          {pessoas.length > MAX_PESSOAS_NO_ROTULO ? ` +${pessoas.length - MAX_PESSOAS_NO_ROTULO}` : ""}
        </text>
      )}
    </>
  );
}

function FormaDoElemento({ elemento, selecionado, podeEditar, onSelecionar, onArrastar }) {
  const tipo = elemento.alvo === ALVO.POSTO ? "Posto" : "Subsetor";
  return (
    <path
      className="forma"
      d={elemento.path}
      data-alvo={elemento.alvo}
      data-elemento={`${elemento.alvo}:${elemento.id}`}
      data-selecionado={selecionado ? "sim" : undefined}
      data-inativo={elemento.ativo ? undefined : "sim"}
      data-fora={elemento.ativo && !elemento.cabe ? "sim" : undefined}
      data-semdesenho={elemento.semDesenho ? "sim" : undefined}
      data-arrastavel={podeEditar ? "sim" : undefined}
      role="button"
      tabIndex={0}
      aria-label={`${tipo} ${elemento.nome}`}
      aria-pressed={selecionado}
      onPointerDown={(e) => {
        onSelecionar(elemento);
        if (podeEditar) onArrastar(e, elemento, "mover");
      }}
      // O clique também seleciona: mouse sem eventos de ponteiro e leitor de
      // tela chegam por aqui, e selecionar duas vezes o mesmo é inofensivo.
      onClick={() => onSelecionar(elemento)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelecionar(elemento);
        }
      }}
    >
      {/* Caixa pequena demais para o rótulo fica sem texto: o nome inteiro
          está aqui, no tooltip, e no painel ao selecionar. */}
      <title>{`${tipo} ${elemento.nome}`}</title>
    </path>
  );
}

/**
 * Alça de redimensionar, na quina de baixo à direita do selecionado: um alvo
 * invisível de 44 px de TELA e, dentro dele, o quadradinho que se vê. Antes a
 * alça tinha ~12 px no tablet — dava para ver, não para pegar.
 */
function AlcaDeTamanho({ caixa, nome, alvo, lado, onArrastar }) {
  const canto = { x: caixa.x + caixa.largura, y: caixa.y + caixa.altura };
  const alvoLado = Math.max(alvo, lado);
  return (
    <g className="alca-grupo">
      <rect
        className="alca-alvo"
        x={canto.x - alvoLado / 2}
        y={canto.y - alvoLado / 2}
        width={alvoLado}
        height={alvoLado}
        role="button"
        tabIndex={-1}
        aria-label={`Redimensionar ${nome}`}
        onPointerDown={onArrastar}
      />
      <rect
        className="alca"
        x={canto.x - lado / 2}
        y={canto.y - lado / 2}
        width={lado}
        height={lado}
        rx={lado / 4}
        pointerEvents="none"
      />
    </g>
  );
}

// -------------------------------------------------------------- painel

function ItensDoElemento({ elemento, demandas, pecas, hoje, onAbrirAmbiente, onAbrirPeca }) {
  const casa = (item) =>
    elemento.alvo === ALVO.POSTO
      ? (item.postoId || null) === elemento.id
      : (item.subsetorId || null) === elemento.id;
  const ambientes = demandas.filter(casa).slice(0, MAX_ITENS_NO_PAINEL);
  const pecasAqui = pecas.filter(casa).slice(0, MAX_ITENS_NO_PAINEL);
  if (ambientes.length === 0 && pecasAqui.length === 0) {
    return <p className="vazio">Nada alocado aqui agora.</p>;
  }
  return (
    <Lista>
      {ambientes.map((d) => (
        <ItemLista key={`a:${d.chaveLinha}`} data-clicavel="sim" onClick={() => onAbrirAmbiente(d)}>
          <span className="principal">
            <span className="l1">{d.rotulo || d.ambiente}</span>
            <span className="l2">{textoDoPrazo(d.prazoEfetivo, hoje).texto}</span>
          </span>
          <PillStatus tom={d.atrasada ? "danger" : "neutral"} ponto={false}>
            {ROTULO_STATUS_AMBIENTE[d.statusApp]}
          </PillStatus>
        </ItemLista>
      ))}
      {pecasAqui.map((p) => (
        <ItemLista key={`p:${p.id}`} data-clicavel="sim" onClick={() => onAbrirPeca(p)}>
          <span className="principal">
            <span className="l1">{p.rotulo}</span>
            <span className="l2">{p.ambienteRotulo || "Ambiente de origem não carregado"}</span>
          </span>
          <PillStatus tom={p.atrasada ? "danger" : "neutral"} ponto={false}>
            {ROTULO_STATUS_PECA[p.status]}
          </PillStatus>
        </ItemLista>
      ))}
    </Lista>
  );
}

/**
 * Excluir de vez o que foi desenhado errado — o desfazer de quem risca a forma
 * no tablet. Só aparece para o que NUNCA carregou trabalho: com ambiente,
 * tarefa, peça ou posto dentro, a saída é desativar (e a história fica). O
 * backend confere de novo e recusa com a frase que a tela mostra; aqui o que
 * se evita é oferecer um botão que sempre falharia.
 *
 * Duas etapas de propósito: exclusão não tem desfazer.
 */
function BlocoExcluir({ elemento, ePosto, impedimento, confirmando, onConfirmar, onDesistir, onExcluir }) {
  const oque = ePosto ? "posto" : "subsetor";
  if (impedimento) return <p className="vazio">{impedimento}</p>;
  if (!confirmando) {
    return (
      <Acoes>
        <AsyncButton variant="danger" silentError onClick={async () => onConfirmar()}>
          Excluir {oque}
        </AsyncButton>
      </Acoes>
    );
  }
  return (
    <>
      <AvisoMapa $tom="danger" role="alert">
        Excluir {elemento.nome} apaga o desenho de vez. Não dá para desfazer.
      </AvisoMapa>
      <Acoes>
        <AsyncButton
          variant="danger"
          silentError
          successMessage={ePosto ? "Posto excluído." : "Subsetor excluído."}
          onClick={onExcluir}
        >
          Excluir mesmo
        </AsyncButton>
        <AsyncButton variant="secondary" silentError onClick={async () => onDesistir()}>
          Manter
        </AsyncButton>
      </Acoes>
    </>
  );
}

function PainelDoElemento({
  elemento,
  cena,
  demandas,
  pecas,
  hoje,
  podeEditar,
  renomeando,
  acoes,
  rodar,
  onAbrirAmbiente,
  onAbrirPeca,
  onTrocarSubsetor,
  onSelecionar,
}) {
  const [nome, setNome] = useState(elemento?.nome || "");
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const campoRef = useRef(null);
  const quem = elemento ? `${elemento.alvo}:${elemento.id}` : "";
  useEffect(() => {
    setNome(elemento?.nome || "");
    // Trocou de elemento: o "tem certeza?" do anterior não vale para este.
    setConfirmandoExclusao(false);
  }, [quem, elemento?.nome]);
  useEffect(() => {
    if (renomeando && campoRef.current) campoRef.current.focus();
  }, [renomeando, quem]);

  if (!elemento) {
    return (
      <PainelElemento aria-label="Elemento selecionado">
        <h3>Nada selecionado</h3>
        <p className="vazio">
          Toque num subsetor ou posto para ver quem trabalha ali e o que está alocado. Para criar, escolha
          Desenhar e risque a forma com o dedo.
        </p>
      </PainelElemento>
    );
  }

  const ePosto = elemento.alvo === ALVO.POSTO;
  const impedimento = motivoParaNaoExcluir(elemento, cena);
  const limpo = limparEspacos(nome);
  const salvarNome = () =>
    rodar(async () => {
      const dados = { nome: limpo, versao: elemento.versao };
      if (ePosto) await acoes.salvarPosto(elemento.id, dados);
      else await acoes.salvarSubsetor(elemento.id, dados);
    });
  const alternarAtivo = () =>
    rodar(async () => {
      const dados = { ativo: !elemento.ativo, versao: elemento.versao };
      if (ePosto) await acoes.salvarPosto(elemento.id, dados);
      else await acoes.salvarSubsetor(elemento.id, dados);
    });
  const excluir = () =>
    rodar(async () => {
      await acoes.excluirCadastro(ePosto ? ALVO.POSTO : ALVO.SUBSETOR, elemento);
      setConfirmandoExclusao(false);
      onSelecionar?.(null);
    });

  return (
    <PainelElemento aria-label="Elemento selecionado">
      <h3>
        {ePosto ? "Posto" : "Subsetor"}: {elemento.nome}
      </h3>
      {!elemento.cabe && (
        <AvisoMapa $tom="danger" role="status">
          {ePosto
            ? `Este posto está fora de ${elemento.paiNome}. Arraste-o para dentro antes de salvar.`
            : "Este subsetor está fora da sala. Arraste-o para dentro antes de salvar."}
        </AvisoMapa>
      )}
      {podeEditar && (
        <>
          <Campo>
            Nome
            <input
              ref={campoRef}
              value={nome}
              maxLength={TAMANHO_NOME}
              aria-label={`Nome de ${elemento.nome}`}
              onChange={(e) => setNome(e.target.value)}
              onBlur={() => setNome((n) => limparEspacos(n))}
            />
          </Campo>
          {ePosto && (
            <Campo>
              Subsetor
              <select
                value={elemento.subsetorId || ""}
                aria-label={`Subsetor de ${elemento.nome}`}
                onChange={(e) => onTrocarSubsetor(elemento, e.target.value || null)}
              >
                <option value="">Fila do setor (sem subsetor)</option>
                {cena.subsetores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                    {s.ativo ? "" : " (desativado)"}
                  </option>
                ))}
              </select>
            </Campo>
          )}
          <Acoes>
            <AsyncButton
              silentError
              disabled={!limpo || limpo === elemento.nome}
              successMessage={ePosto ? "Posto renomeado." : "Subsetor renomeado."}
              onClick={salvarNome}
            >
              Salvar nome
            </AsyncButton>
            <AsyncButton
              variant="secondary"
              silentError
              successMessage={elemento.ativo ? "Desativado." : "Reativado."}
              onClick={alternarAtivo}
            >
              {elemento.ativo ? "Desativar" : "Reativar"}
            </AsyncButton>
          </Acoes>
          <BlocoExcluir
            elemento={elemento}
            ePosto={ePosto}
            impedimento={impedimento}
            confirmando={confirmandoExclusao}
            onConfirmar={() => setConfirmandoExclusao(true)}
            onDesistir={() => setConfirmandoExclusao(false)}
            onExcluir={excluir}
          />
        </>
      )}
      {ePosto && (
        <div>
          <h3>Quem trabalha aqui</h3>
          {elemento.pessoas.length === 0 ? (
            <p className="vazio">Ninguém vinculado. Vincule em Configurar setor, aba Postos.</p>
          ) : (
            <p className="vazio">{elemento.pessoas.map((p) => p.nome).join(", ")}</p>
          )}
        </div>
      )}
      <div>
        <h3>Alocado aqui</h3>
        <ItensDoElemento
          elemento={elemento}
          demandas={demandas}
          pecas={pecas}
          hoje={hoje}
          onAbrirAmbiente={onAbrirAmbiente}
          onAbrirPeca={onAbrirPeca}
        />
      </div>
    </PainelElemento>
  );
}

// ------------------------------------------------------------ componente

/**
 * Props: setor (do catálogo), estado (useEstadoMapa), demandas, tarefas,
 * pecas (todas já do setor), indice, hoje, podeEditar (gerir), modo, onModo,
 * acoes (useAcoesDoSetor), onRecarregar, onAbrirAmbiente(demanda),
 * onAbrirPeca(peca), onConfigurar?.
 */
export default function MapaDoSetor({
  setor,
  estado,
  demandas = LISTA_VAZIA,
  tarefas = LISTA_VAZIA,
  pecas = LISTA_VAZIA,
  indice,
  hoje,
  podeEditar = false,
  modo = MODO.ORGANIZAR,
  onModo,
  acoes,
  onRecarregar,
  onAbrirAmbiente,
  onAbrirPeca,
}) {
  const { erro, rodar, limpar } = useErroDeAcao();
  const [rascunho, setRascunho] = useState({ formas: {}, pais: {} });
  const [selecao, setSelecao] = useState(null);
  const [renomeando, setRenomeando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [traco, setTraco] = useState(null);
  // Retângulo do <svg> na tela: é dele que sai a escala tela↔mundo da alça e
  // do traço (o viewBox muda de tamanho conforme a sala).
  const [medida, setMedida] = useState(null);
  const svgRef = useRef(null);
  const arrasteRef = useRef(null);


  const cena = useMemo(
    () =>
      cenaDoSetor(
        {
          setor,
          subsetores: estado?.subsetores,
          postos: estado?.postos,
          demandas,
          tarefas,
          pecas,
          indice,
          hoje,
        },
        rascunho
      ),
    [setor, estado?.subsetores, estado?.postos, demandas, tarefas, pecas, indice, hoje, rascunho]
  );

  const mudancas = useMemo(
    () => mudancasParaSalvar(cena, { setor, subsetores: estado?.subsetores, postos: estado?.postos }),
    [cena, setor, estado?.subsetores, estado?.postos]
  );

  const selecionado = useMemo(() => {
    if (!selecao) return null;
    const lista = selecao.alvo === ALVO.POSTO ? cena.postos : cena.subsetores;
    return lista.find((e) => e.id === selecao.id) || null;
  }, [selecao, cena]);

  const fonte = fonteDaVisao(cena.visao);
  // A escala tela↔mundo só existe depois que o SVG foi medido; até lá vale 1.
  const escalaTela = escalaDaTela(medida, cena.visao);
  const emMundo = useCallback((px) => px / escalaTela, [escalaTela]);

  // O quadro é medido depois de cada desenho (a altura dele vem da PROPORÇÃO
  // da sala, então mudar a forma muda o tamanho na tela) e sempre que o
  // container mudar de largura sem passar pelo React. Guardar a medida só
  // quando ela muda de verdade é o que impede o laço de renderização.
  const medir = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const novo = svg.getBoundingClientRect();
    setMedida((atual) => (atual && atual.width === novo.width && atual.height === novo.height ? atual : novo));
  }, []);
  useLayoutEffect(medir);
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof ResizeObserver === "undefined") return undefined;
    const observador = new ResizeObserver(medir);
    observador.observe(svg);
    return () => observador.disconnect();
  }, [medir]);

  const paraMundoNaTela = useCallback(
    (evento) => {
      const svg = svgRef.current;
      if (!svg) return null;
      return paraMundo(evento, svg.getBoundingClientRect(), cena.visao);
    },
    [cena.visao]
  );

  const selecionar = useCallback((elemento) => {
    setSelecao(elemento ? { alvo: elemento.alvo, id: elemento.id } : null);
    setRenomeando(false);
  }, []);

  /**
   * Aplica uma forma nova a um elemento, validando a hierarquia. Devolve o
   * aviso que a tela mostra — o elemento nunca "desaparece": ou ele entra no
   * pai (encaixado, se preciso), ou volta para onde estava.
   */
  const aplicarForma = useCallback(
    (elemento, formaNova, formaAnterior) => {
      const proposta = normalizar(formaNova);
      if (!proposta) return;
      const ePosto = elemento.alvo === ALVO.POSTO;
      const paiId = ePosto ? paiDoPosto(proposta, cena.subsetores) : null;
      const pai = ePosto ? cena.subsetores.find((s) => s.id === paiId) : null;
      const formaPai = pai ? pai.forma : cena.setor?.forma;
      const nomePai = pai ? pai.nome : setor?.nome || "a sala";
      const veredito = validarNoPai(proposta, formaPai, { nomeFilho: elemento.nome, nomePai });
      if (!veredito.ok) {
        if (formaAnterior) {
          setRascunho((r) => comForma(r, elemento.alvo, elemento.id, formaAnterior, elemento.subsetorSalvo));
        }
        setAviso({ tom: "danger", texto: veredito.mensagem });
        return;
      }
      setRascunho((r) => comForma(r, elemento.alvo, elemento.id, veredito.forma, ePosto ? paiId : undefined));
      if (veredito.ajustada) setAviso({ tom: "warning", texto: veredito.mensagem });
      else if (ePosto && paiId !== elemento.subsetorId) {
        setAviso({ tom: "info", texto: `${elemento.nome} agora fica em ${nomePai}.` });
      } else setAviso(null);
    },
    [cena.subsetores, cena.setor, setor]
  );

  const iniciarArraste = useCallback(
    (evento, elemento, tipo) => {
      if (!podeEditar || modo !== MODO.ORGANIZAR) return;
      const ponto = paraMundoNaTela(evento);
      if (!ponto) return;
      evento.stopPropagation();
      if (evento.currentTarget.setPointerCapture) {
        try {
          evento.currentTarget.setPointerCapture(evento.pointerId);
        } catch {
          // Navegador sem captura de ponteiro: o arraste ainda funciona pelos
          // eventos do SVG (é o caso do jsdom nos testes).
        }
      }
      arrasteRef.current = {
        alvo: elemento.alvo,
        id: elemento.id,
        tipo,
        inicio: ponto,
        formaInicial: elemento.forma,
        caixaInicial: caixaEnvolvente(elemento.forma),
        moveu: false,
      };
    },
    [podeEditar, modo, paraMundoNaTela]
  );

  const aoDescer = useCallback(
    (evento) => {
      const ponto = paraMundoNaTela(evento);
      if (!ponto) return;
      if (modo !== MODO.ORGANIZAR && podeEditar) {
        setTraco([ponto]);
        setAviso(null);
        return;
      }
      // Toque no vazio da sala: perde a seleção (é o gesto esperado).
      if (!elementoNoPonto(cena, ponto)) selecionar(null);
    },
    [modo, podeEditar, paraMundoNaTela, cena, selecionar]
  );

  const aoMover = useCallback(
    (evento) => {
      if (traco) {
        const ponto = paraMundoNaTela(evento);
        if (ponto) setTraco((atual) => [...atual, ponto]);
        return;
      }
      const arraste = arrasteRef.current;
      if (!arraste) return;
      const ponto = paraMundoNaTela(evento);
      if (!ponto) return;
      const dx = ponto.x - arraste.inicio.x;
      const dy = ponto.y - arraste.inicio.y;
      if (!arraste.moveu && Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      arraste.moveu = true;
      const nova =
        arraste.tipo === "mover"
          ? mover(arraste.formaInicial, dx, dy)
          : redimensionar(arraste.formaInicial, {
              largura: Math.max(LIMITES_FORMA.LADO_MINIMO, arraste.caixaInicial.largura + dx),
              altura: Math.max(LIMITES_FORMA.LADO_MINIMO, arraste.caixaInicial.altura + dy),
            });
      if (nova) setRascunho((r) => comForma(r, arraste.alvo, arraste.id, nova));
    },
    [traco, paraMundoNaTela]
  );

  const criarPorDesenho = useCallback(
    async (forma) => {
      const eSubsetor = modo === MODO.SUBSETOR;
      const paiId = eSubsetor ? null : paiDoPosto(forma, cena.subsetores);
      const pai = eSubsetor ? null : cena.subsetores.find((s) => s.id === paiId);
      const formaPai = pai ? pai.forma : cena.setor?.forma;
      const nomeFilho = eSubsetor ? "O subsetor" : "O posto";
      const nomePai = pai ? pai.nome : setor?.nome || "a sala";
      const veredito = validarNoPai(forma, formaPai, { nomeFilho, nomePai });
      if (!veredito.ok) {
        setAviso({ tom: "danger", texto: veredito.mensagem });
        return;
      }
      const nome = eSubsetor
        ? proximoNome("Subsetor", cena.subsetores.length)
        : proximoNome("Posto", cena.postos.length);
      const criado = await rodar(() =>
        eSubsetor
          ? acoes.salvarSubsetor(null, {
              setor_id: setor.id,
              nome,
              ordem: cena.subsetores.length + 1,
              forma: veredito.forma,
            })
          : acoes.salvarPosto(null, {
              setor_id: setor.id,
              subsetor_id: paiId,
              nome,
              colaborador_ids: [],
              ordem: cena.postos.length + 1,
              forma: veredito.forma,
            })
      );
      if (criado?.id) {
        setSelecao({ alvo: eSubsetor ? ALVO.SUBSETOR : ALVO.POSTO, id: criado.id });
        setRenomeando(true);
      }
      setAviso({
        tom: "info",
        texto: `${nome} criado${veredito.ajustada ? " e encaixado" : ""} em ${nomePai}. Dê o nome que a fábrica usa.`,
      });
    },
    [modo, cena, setor, acoes, rodar]
  );

  const aoSubir = useCallback(
    () => {
      if (traco) {
        const pontos = traco;
        setTraco(null);
        // Toque trêmulo também produz três ou quatro pontos: o que separa
        // desenho de toque é o TAMANHO do traço na tela, não a contagem.
        const forma = pontos.length < TRACO_MINIMO
          ? null
          : corrigirTracado(pontos, { extensaoMinima: emMundo(TRACO_MINIMO_PX) });
        if (!forma) {
          setAviso({ tom: "warning", texto: "Traço curto demais. Risque a forma inteira com o dedo." });
          return;
        }
        criarPorDesenho(forma).catch(() => {});
        return;
      }
      const arraste = arrasteRef.current;
      arrasteRef.current = null;
      if (!arraste || !arraste.moveu) return;
      const lista = arraste.alvo === ALVO.POSTO ? cena.postos : cena.subsetores;
      const elemento = lista.find((e) => e.id === arraste.id);
      // A captura de ponteiro é liberada pelo navegador no pointerup: não há
      // o que desfazer aqui além de validar onde o elemento parou.
      if (elemento) aplicarForma(elemento, elemento.forma, arraste.formaInicial);
    },
    [traco, cena, aplicarForma, criarPorDesenho, emMundo]
  );

  const aoTeclar = useCallback(
    (evento) => {
      if (!podeEditar || !selecionado) return;
      const passos = {
        ArrowLeft: [-PASSO_TECLADO, 0],
        ArrowRight: [PASSO_TECLADO, 0],
        ArrowUp: [0, -PASSO_TECLADO],
        ArrowDown: [0, PASSO_TECLADO],
      };
      const passo = passos[evento.key];
      if (!passo) return;
      evento.preventDefault();
      const [dx, dy] = passo;
      const caixa = caixaEnvolvente(selecionado.forma);
      const nova = evento.shiftKey
        ? redimensionar(selecionado.forma, {
            largura: Math.max(LIMITES_FORMA.LADO_MINIMO, caixa.largura + dx),
            altura: Math.max(LIMITES_FORMA.LADO_MINIMO, caixa.altura + dy),
          })
        : mover(selecionado.forma, dx, dy);
      if (nova) aplicarForma(selecionado, nova, selecionado.forma);
    },
    [podeEditar, selecionado, aplicarForma]
  );

  const trocarSubsetor = useCallback(
    (posto, subsetorId) =>
      rodar(() =>
        acoes.salvarPosto(posto.id, { subsetor_id: subsetorId || null, versao: posto.versao })
      ).then(() => setAviso({ tom: "info", texto: `${posto.nome} mudou de subsetor.` })),
    [acoes, rodar]
  );

  const salvar = () =>
    rodar(async () => {
      for (const posto of mudancas.reparentados) {
        // eslint-disable-next-line no-await-in-loop
        await acoes.salvarPosto(posto.id, {
          subsetor_id: posto.subsetor_id,
          forma: posto.forma,
          versao: posto.versao,
        });
      }
      if (mudancas.formas.length) await acoes.salvarFormas(mudancas.formas);
      await onRecarregar?.();
      setRascunho({ formas: {}, pais: {} });
      // O aviso de sucesso e o toast do AsyncButton: repetir aqui seria a mesma
      // mensagem duas vezes na tela.
      setAviso(null);
    });

  const descartar = () => {
    setRascunho({ formas: {}, pais: {} });
    setAviso(null);
    limpar();
  };

  useEffect(() => {
    // Trocar de modo limpa o traço pendente: desenhar e mudar de ideia no meio
    // do gesto não pode criar nada.
    setTraco(null);
  }, [modo]);

  const temPendencia = mudancas.total > 0;
  const naoCabe = cena.fora.length > 0;
  const idGrade = `grade-${setor?.id || "setor"}`;

  return (
    <div>
      {(podeEditar || temPendencia) && (
        <BarraMapa>
          {podeEditar && <SeletorSegmentado rotulo="O que fazer no mapa" opcoes={MODOS} valor={modo} onChange={onModo} />}
          <Espaco />
          {temPendencia && (
            <>
              <span className="pendencia">Sem salvar: {textoDoRascunho(mudancas)}</span>
              <button type="button" className="btn btn--ghost btn--sm" onClick={descartar}>
                <FiRotateCcw size={14} aria-hidden="true" /> Descartar
              </button>
              <AsyncButton silentError disabled={naoCabe} successMessage="Desenho salvo." onClick={salvar}>
                <FiSave size={14} aria-hidden="true" /> Salvar desenho
              </AsyncButton>
            </>
          )}
        </BarraMapa>
      )}

      <ErroDaAcao erro={erro} onRecarregar={onRecarregar} />

      <QuadroMapa>
        <div style={{ minWidth: 0 }}>
          <Planta
            data-modo={podeEditar ? modo : "organizar"}
            style={{ "--proporcao": `${cena.visao.caixa.largura} / ${cena.visao.caixa.altura}` }}
          >
            <svg
              ref={svgRef}
              viewBox={cena.visao.viewBox}
              preserveAspectRatio="xMidYMid meet"
              role="group"
              aria-label={`Planta do setor ${setor?.nome || ""}`}
              tabIndex={0}
              onPointerDown={aoDescer}
              onPointerMove={aoMover}
              onPointerUp={aoSubir}
              onPointerCancel={() => {
                setTraco(null);
                arrasteRef.current = null;
              }}
              onKeyDown={aoTeclar}
            >
              <defs>
                <pattern id={idGrade} width={fonte * 4} height={fonte * 4} patternUnits="userSpaceOnUse">
                  <path className="grade" d={`M ${fonte * 4} 0 L 0 0 0 ${fonte * 4}`} fill="none" />
                </pattern>
              </defs>
              {cena.setor && (
                <>
                  <path className="sala" d={cena.setor.path} />
                  <path d={cena.setor.path} fill={`url(#${idGrade})`} stroke="none" pointerEvents="none" />
                </>
              )}
              {cena.subsetores.map((s) => (
                <FormaDoElemento
                  key={s.id}
                  elemento={s}
                  selecionado={selecao?.alvo === ALVO.SUBSETOR && selecao.id === s.id}
                  podeEditar={podeEditar}
                  onSelecionar={selecionar}
                  onArrastar={iniciarArraste}
                />
              ))}
              {cena.subsetores.map((s) => (
                <RotuloDoElemento key={`r:${s.id}`} elemento={s} fonte={fonte} />
              ))}
              {cena.postos.map((p) => (
                <FormaDoElemento
                  key={p.id}
                  elemento={p}
                  selecionado={selecao?.alvo === ALVO.POSTO && selecao.id === p.id}
                  podeEditar={podeEditar}
                  onSelecionar={selecionar}
                  onArrastar={iniciarArraste}
                />
              ))}
              {cena.postos.map((p) => (
                <RotuloDoElemento key={`r:${p.id}`} elemento={p} fonte={fonte} />
              ))}
              {selecionado?.caixa && (
                <path className="anel" d={selecionado.path} pointerEvents="none" />
              )}
              {podeEditar && modo === MODO.ORGANIZAR && selecionado?.caixa && (
                <AlcaDeTamanho
                  caixa={selecionado.caixa}
                  nome={selecionado.nome}
                  alvo={emMundo(ALVO_ALCA_PX)}
                  lado={emMundo(ALCA_VISIVEL_PX)}
                  onArrastar={(e) => iniciarArraste(e, selecionado, "redimensionar")}
                />
              )}
              {traco && (
                <path className="tracando" d={caminhoDoTraco(traco)} strokeWidth={emMundo(LARGURA_TRACO_PX)} />
              )}
            </svg>
          </Planta>

          <LegendaMapa>
            <li>
              <span data-amostra="sala" aria-hidden="true" /> Sala do setor
            </li>
            <li>
              <span data-amostra="subsetor" aria-hidden="true" /> Subsetor
            </li>
            <li>
              <span data-amostra="posto" aria-hidden="true" /> Posto
            </li>
            <li>
              <span data-amostra="fora" aria-hidden="true" /> Fora do lugar
            </li>
          </LegendaMapa>

          {aviso && (
            <AvisoMapa $tom={aviso.tom} role="status">
              {aviso.texto}
            </AvisoMapa>
          )}
          {cena.semDesenho && (
            <AvisoMapa $tom="neutral" role="status">
              Esta sala ainda não foi desenhada no mapa geral: a planta acima é o retângulo padrão. Desenhe a
              sala no Mapa da Produção para o contorno bater com a fábrica.
            </AvisoMapa>
          )}
          {podeEditar && cena.subsetores.length === 0 && (
            <AvisoMapa $tom="neutral" role="status">
              Nenhum subsetor ainda. Escolha Desenhar subsetor e risque a área dele com o dedo dentro da sala; o
              traço torto é endireitado ao soltar. Pelo teclado, crie em Configurar setor e posicione aqui com as
              setas (Shift + setas redimensiona).
            </AvisoMapa>
          )}
        </div>

        <PainelDoElemento
          elemento={selecionado}
          cena={cena}
          demandas={demandas}
          pecas={pecas}
          hoje={hoje}
          podeEditar={podeEditar}
          renomeando={renomeando}
          acoes={acoes}
          rodar={rodar}
          onAbrirAmbiente={onAbrirAmbiente}
          onAbrirPeca={onAbrirPeca}
          onTrocarSubsetor={trocarSubsetor}
          onSelecionar={selecionar}
        />
      </QuadroMapa>
    </div>
  );
}
